import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Input } from "@remo-co/ui-core/src/components/Input";
import { useI18n } from "i18n";
import { Link } from "@remo-co/ui-core/src/components/Link";
import { useSelector } from "react-redux";
import { selectCustomEmailSenderSettings } from "modules/companySettings/redux/selectors";
import { useContext } from "react";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import useCompany from "modules/company/hooks/useCompany";
import { selectUserId } from "modules/auth/redux/selectors";
import { Alert, AlertTitle } from "@remo-co/ui-core/src/components/Alert";
import { CustomEmailSenderState } from "modules/company/types";
import { useStyles } from "./styles";

interface Props {
  enabledAgencyWhiteLabelling: boolean;
}

export const EmailSenderSettings = ({ enabledAgencyWhiteLabelling }: Props) => {
  const { company } = useCompany();
  const userId = useSelector(selectUserId);
  const customEmailSenderSettings = useSelector(
    selectCustomEmailSenderSettings,
  );
  const { track } = useContext(TRACKING_CONTEXT);
  const { t } = useI18n(["settings", "url", "common"]);
  const styles = useStyles();

  const onRequestClick = () => {
    if (company && company.id && userId) {
      track(Events.CUSTOM_EMAIL_SENDER_REQUESTED, {
        companyIds: company.id,
        userId,
      });
    }
  };

  const onChangeRequestClick = () => {
    if (company && company.id && userId) {
      track(Events.CUSTOM_EMAIL_SENDER_CHANGE_REQUESTED, {
        companyIds: company.id,
        userId,
      });
    }
  };

  const getAlertSeverity = (status: CustomEmailSenderState) => {
    switch (status) {
      case CustomEmailSenderState.VERIFIED:
        return "success";
      case CustomEmailSenderState.REQUESTED:
        return "info";
      case CustomEmailSenderState.AWAITING_CONFIRMATION:
        return "warning";
      default:
        return "info";
    }
  };

  return (
    <Box className={styles.container}>
      <Typography variant="body1">
        {t("settings:email.sending.settings")}
      </Typography>
      <Box className={styles.content}>
        {!customEmailSenderSettings ? (
          <Box>
            <Typography variant="body2" className={styles.subtitle}>
              {t("settings:email.sending.settings.subtitle")}
            </Typography>
            <Button<"a">
              variant="secondary"
              color="blue"
              size="md"
              data-testid="request-email-sending-settings-button"
              href={t("url:request.custom.domain.email.sender.link")}
              disabled={!enabledAgencyWhiteLabelling}
              className={styles.requestButton}
              onClick={onRequestClick}
            >
              {t("settings:request.email.sending.settings")}
            </Button>
          </Box>
        ) : (
          <Box>
            <Alert
              className={styles.alert}
              severity={getAlertSeverity(customEmailSenderSettings.status)}
            >
              {customEmailSenderSettings.status ===
              CustomEmailSenderState.VERIFIED ? (
                <>
                  {t(
                    `settings:email.sender.settings.${customEmailSenderSettings.status}`,
                  )}
                </>
              ) : (
                <>
                  <AlertTitle>
                    {t(
                      `settings:email.sender.settings.${customEmailSenderSettings.status}`,
                    )}
                  </AlertTitle>
                  <Typography variant="caption">
                    {t(
                      `settings:email.sender.settings.${customEmailSenderSettings.status}.subtitle`,
                    )}
                  </Typography>
                </>
              )}
            </Alert>
            <Typography variant="subtitle1">
              {t("settings:sender.email.address")}
            </Typography>
            <Input
              size="sm"
              value={customEmailSenderSettings.email}
              disabled
              className={styles.settingInput}
              data-testid="custom-email-sender-email-address"
            />
            <Typography variant="subtitle1">
              {t("settings:from.name")}
            </Typography>
            <Input
              size="sm"
              value={customEmailSenderSettings.fromName}
              disabled
              className={styles.settingInput}
              data-testid="custom-email-sender-from-name"
            />
            <Typography variant="subtitle1">
              {t("settings:reply.to.email.address")}
            </Typography>
            <Input
              size="sm"
              value={customEmailSenderSettings.replyTo || "N/A"}
              disabled
              className={styles.settingInput}
              data-testid="custom-email-sender-reply-to-email-address"
            />
            <Button<"a">
              variant="secondary"
              color="blue"
              size="md"
              data-testid="request-email-sending-settings-change-button"
              href={t("url:request.custom.domain.email.sender.link")}
              disabled={!enabledAgencyWhiteLabelling}
              className={styles.requestChangesButton}
              onClick={onChangeRequestClick}
            >
              {t("settings:request.email.sending.settings.change")}
            </Button>
          </Box>
        )}
      </Box>
      <Link
        openInNewTab
        href={t("url:custom.email.sender.help.article")}
        data-testid="email-sending-settings-help-link"
        aria-label={t("settings:email.sending.settings.help.link")}
      >
        <Typography
          variant="body1"
          component="span"
          underline
          className={styles.helpLink}
        >
          {t("settings:email.sending.settings.help.link")}
        </Typography>
      </Link>
    </Box>
  );
};
